import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import Analytics from "./components/Analytics";
import { AuthProvider } from "./hooks/useAuth";
import { ThemeProvider } from "./hooks/useTheme";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminHubLayout from "./components/admin/hub/AdminHubLayout";

// A home é carregada de forma eager por ser a página de entrada (LCP).
// Todas as demais rotas são divididas em chunks sob demanda (code splitting),
// isolando dependências pesadas (mapbox-gl, html5-qrcode, xlsx, jspdf, recharts)
// fora do bundle inicial.
import Index from "./pages/Index";
const Rankings = lazy(() => import("./pages/Rankings"));
const Federacao = lazy(() => import("./pages/Federacao"));
const Eventos = lazy(() => import("./pages/Eventos"));
const Boxes = lazy(() => import("./pages/Boxes"));
const Filiacao = lazy(() => import("./pages/Filiacao"));
const Noticias = lazy(() => import("./pages/Noticias"));
const NoticiaDetalhe = lazy(() => import("./pages/NoticiaDetalhe"));
const Auth = lazy(() => import("./pages/Auth"));
const AdminEventos = lazy(() => import("./pages/AdminEventos"));
const AdminUsuarios = lazy(() => import("./pages/AdminUsuarios"));
const PublicLeaderboard = lazy(() => import("./pages/PublicLeaderboard"));
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const WodArena = lazy(() => import("./pages/WodArena"));
const Ingressos = lazy(() => import("./pages/Ingressos"));
const ValidarTicket = lazy(() => import("./pages/ValidarTicket"));
const TermosUso = lazy(() => import("./pages/TermosUso"));
const PillarsABTest = lazy(() => import("./pages/PillarsABTest"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminVendas = lazy(() => import("./pages/AdminVendas"));
const AdminScanner = lazy(() => import("./pages/AdminScanner"));
const PoliticasIngressos = lazy(() => import("./pages/PoliticasIngressos"));
const RegulamentoHibridoGames = lazy(() => import("./pages/RegulamentoHibridoGames"));
const AthleteDashboard = lazy(() => import("./pages/AthleteDashboard"));
const ValidarAtleta = lazy(() => import("./pages/ValidarAtleta"));
const AdminAtletas = lazy(() => import("./pages/AdminAtletas"));
const AdminBoxes = lazy(() => import("./pages/AdminBoxes"));
const AdminMercadoPago = lazy(() => import("./pages/AdminMercadoPago"));
const AdminTiposIngresso = lazy(() => import("./pages/AdminTiposIngresso"));
const Transparencia = lazy(() => import("./pages/Transparencia"));
const AdminFinanceiro = lazy(() => import("./pages/AdminFinanceiro"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Chamamento2027 = lazy(() => import("./pages/Chamamento2027"));
const AdminCandidaturas = lazy(() => import("./pages/AdminCandidaturas"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div
      className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-primary"
      role="status"
      aria-label="Carregando"
    />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ThemeProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Analytics />
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/rankings" element={<Rankings />} />
              <Route path="/ingressos" element={<Ingressos />} />
              <Route path="/politicas-ingressos" element={<PoliticasIngressos />} />
              <Route path="/regulamento-hibrido-games-26-2" element={<RegulamentoHibridoGames />} />
              <Route path="/validar-ticket" element={<ValidarTicket />} />
              <Route path="/federacao" element={<Federacao />} />
              <Route path="/eventos" element={<Eventos />} />
              <Route path="/chamamento-2027" element={<Chamamento2027 />} />
              {/* Link curto para Instagram e WhatsApp */}
              <Route path="/chamamento" element={<Navigate to="/chamamento-2027" replace />} />
              <Route path="/boxes" element={<Boxes />} />
              <Route path="/filiacao" element={<Filiacao />} />
              <Route path="/noticias" element={<Noticias />} />
              <Route path="/noticias/:slug" element={<NoticiaDetalhe />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
              <Route path="/termos-de-uso" element={<TermosUso />} />
              <Route path="/transparencia" element={<Transparencia />} />
              
              {/* ── Centro de Gestão Admin (hub unificado) ── */}
              <Route path="/admin" element={<AdminHubLayout />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="federacao/atletas" element={<AdminAtletas />} />
                <Route path="federacao/boxes" element={<AdminBoxes />} />
                <Route path="financeiro" element={<AdminFinanceiro />} />
                <Route path="eventos" element={<AdminEventos />} />
                <Route path="wod-arena" element={<WodArena />} />
                <Route path="candidaturas" element={<AdminCandidaturas />} />
                <Route path="ingressos/vendas" element={<AdminVendas />} />
                <Route path="ingressos/tipos" element={<AdminTiposIngresso />} />
                <Route path="ingressos/scanner" element={<AdminScanner />} />
                <Route path="pagamentos/mercado-pago" element={<AdminMercadoPago />} />
                <Route path="usuarios" element={<AdminUsuarios />} />
              </Route>

              {/* Aliases de compatibilidade com as rotas antigas */}
              <Route path="/admin/atletas" element={<Navigate to="/admin/federacao/atletas" replace />} />
              <Route path="/admin/boxes" element={<Navigate to="/admin/federacao/boxes" replace />} />
              <Route path="/admin/eventos" element={<Navigate to="/admin/eventos" replace />} />
              <Route path="/admin/vendas" element={<Navigate to="/admin/ingressos/vendas" replace />} />
              <Route path="/admin/scanner" element={<Navigate to="/admin/ingressos/scanner" replace />} />
              <Route path="/admin/tipos-ingresso" element={<Navigate to="/admin/ingressos/tipos" replace />} />
              <Route path="/admin/mercado-pago" element={<Navigate to="/admin/pagamentos/mercado-pago" replace />} />
              <Route path="/admin/mercado-pago/callback" element={<Navigate to="/admin/pagamentos/mercado-pago" replace />} />
              <Route path="/admin/usuarios" element={<Navigate to="/admin/usuarios" replace />} />
              <Route path="/wod-arena" element={<Navigate to="/admin/wod-arena" replace />} />
              
              <Route path="/leaderboard/:slug" element={<PublicLeaderboard />} />
              <Route path="/pillars-ab-test" element={<PillarsABTest />} />
              <Route path="/dashboard/atleta" element={<AthleteDashboard />} />
              <Route path="/validar-atleta/:fpfId" element={<ValidarAtleta />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
